import React from "react";
import {
  data_type_semantic,
  datatype_display,
  PhysicalKind,
  semantic_type_display,
} from "target_wasm";
import type { ColumnPreview } from "components/canvas/leftSide/import/Connections/tablePreview";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import LinkIcon from "@mui/icons-material/Link";
import { Tooltip } from "components/common/Tooltip";
import { TooltipButton } from "components/common/TooltipButton";
import { ColumnFormatBadge } from "./ColumnList/ColumnFormatBadge";

const TYPE_TONE = {
  muted: "text-mono-small-muted",
  strong: "text-mono-small",
  error: "text-mono-small-error",
};

/** A row's marker: lit while what it opens is open, and never a row click. */
const Marker: React.FC<{
  title?: React.ReactNode;
  label: string;
  active?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
  testId: string;
  children: React.ReactNode;
}> = ({ title, label, active = false, onClick, testId, children }) => (
  <TooltipButton
    title={title}
    variant="contextual"
    size="xs"
    onClick={(e) => {
      e.stopPropagation();
      onClick?.(e);
    }}
    onPointerDown={(e) => e.stopPropagation()}
    onMouseDown={(e) => e.stopPropagation()}
    data-testid={testId}
    data-active={active ? "true" : undefined}
    aria-label={label}
    aria-expanded={onClick ? active : undefined}
    className={`w-auto! min-w-0! px-1! gap-0.5 ${active ? "text-accent bg-accent/15" : "text-fg-muted"}`}
  >
    {children}
  </TooltipButton>
);

/** A column that carries a description; no tooltip unless `title` is its text. */
export const DescriptionMarker: React.FC<{
  title?: string;
  active?: boolean;
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
}> = ({ title, active, onClick }) => (
  <Marker
    title={title}
    label={title ?? "Has a description"}
    active={active}
    onClick={onClick}
    testId="column-description-badge"
  >
    <NotesOutlinedIcon />
  </Marker>
);

/** "🔗 N": the columns this one is linked to. */
export const LinksMarker: React.FC<{
  count: number;
  active?: boolean;
  onClick?: () => void;
}> = ({ count, active, onClick }) => {
  const label = `${count} linked ${count === 1 ? "column" : "columns"}`;
  return (
    <Marker
      title={label}
      label={label}
      active={active}
      onClick={onClick}
      testId="linked-column-badge"
    >
      <LinkIcon />
      <span className="text-xs font-mono tabular-nums">{count}</span>
    </Marker>
  );
};

/** A column's type as every column list names it, its storage on hover. */
export const ColumnType: React.FC<{
  kind: PhysicalKind | undefined;
  typeLabel?: string;
  tone?: keyof typeof TYPE_TONE;
  stored?: boolean;
}> = ({ kind, typeLabel, tone = "muted", stored = false }) =>
  kind === undefined || kind === PhysicalKind.ND ? (
    <span className={TYPE_TONE[tone === "error" ? "error" : "muted"]}>
      {kind === undefined ? typeLabel : "ND"}
    </span>
  ) : (
    <Tooltip title={`Stored as: ${datatype_display(kind)}`}>
      <span className={`transition-colors duration-1000 ${TYPE_TONE[tone]}`}>
        {stored
          ? datatype_display(kind)
          : semantic_type_display(data_type_semantic(kind))}
      </span>
    </Tooltip>
  );

/** A column as every column list draws it: its name, over its type and badges. */
export const ColumnLines: React.FC<{
  name: React.ReactNode;
  actions?: React.ReactNode;
  kind: PhysicalKind | undefined;
  /** Shown as given for a kind this client does not know. */
  typeLabel?: string;
  /** `strong` marks a locked type. */
  tone?: keyof typeof TYPE_TONE;
  /** The storage type over the semantic one, where only storage changed. */
  stored?: boolean;
  lead?: React.ReactNode;
  badges?: React.ReactNode;
  typeClassName?: string;
}> = ({
  name,
  actions,
  kind,
  typeLabel,
  tone = "muted",
  stored = false,
  lead,
  badges,
  typeClassName = "",
}) => (
  <>
    <div className="flex flex-row items-center w-full h-5">
      {name}
      {actions && (
        <div className="flex-none flex items-center gap-1 ml-auto">
          {actions}
        </div>
      )}
    </div>
    <div
      className={`flex items-center gap-1.5 cursor-default w-full h-4 leading-4 whitespace-nowrap overflow-hidden ${typeClassName}`}
    >
      {lead}
      <ColumnType
        kind={kind}
        typeLabel={typeLabel}
        tone={tone}
        stored={stored}
      />
      {badges}
    </div>
  </>
);

/** A column that cannot be edited here: a workbook preview, a table outside this model. */
export const ColumnRow: React.FC<{
  col: ColumnPreview;
  /** No side inset, so names line up with a heading above the list. */
  flush?: boolean;
}> = ({ col, flush = false }) => (
  <li
    className={`flex flex-col justify-center w-full h-11 text-fg-medium py-1 cursor-default${flush ? "" : " section-row"}`}
  >
    <ColumnLines
      name={
        <span className="grow min-w-0 truncate pr-2 text-fg-regular">
          {col.name}
        </span>
      }
      kind={PhysicalKind[col.kind as keyof typeof PhysicalKind]}
      typeLabel={col.typeLabel}
      badges={
        col.unit && (
          <ColumnFormatBadge label={col.unit} tooltip={`Unit: ${col.unit}`} />
        )
      }
    />
  </li>
);
